import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styled from "styled-components";

import { loadMathJax } from "components/organisms/manuscript/editor/visual/rendering/typesetting";

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 90;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.48);
`;

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    width: min(860px, calc(100vw - 32px));
    height: min(680px, calc(100vh - 32px));
    border: 1px solid #d4dee8;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 20px 36px rgba(15, 23, 42, 0.24);
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
    border-bottom: 1px solid #e2e8f0;
`;

const Title = styled.div`
    color: #17314a;
    font-size: 16px;
    font-weight: 800;
`;

const Body = styled.div`
    flex: 1;
    display: grid;
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    gap: 16px;
    min-height: 0;
    padding: 16px 18px;
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
`;

const ImageColumn = styled(Column)`
    .math-ocr-drop-zone {
        flex: 1;
    }
`;

const ResultColumn = styled(Column)`
    gap: 12px;
`;

const SectionTitle = styled.div`
    color: #203249;
    font-size: 12px;
    font-weight: 800;
`;

const DropZone = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
    padding: 18px;
    border: 1px dashed #b9c8d8;
    border-radius: 12px;
    background: #f8fafc;
    color: #5f7288;
    font-size: 13px;
    font-weight: 700;
    text-align: center;
    cursor: pointer;
`;

const PreviewImage = styled.img`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
`;

const Textarea = styled.textarea`
    flex: 1;
    min-height: 180px;
    padding: 12px;
    border: 1px solid #d6e0eb;
    border-radius: 12px;
    color: #203249;
    font-size: 13px;
    line-height: 1.6;
    resize: none;

    &:focus {
        outline: 2px solid rgba(15, 143, 111, 0.18);
        border-color: #0f8f6f;
    }
`;

const ResultSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
    flex: 1;
`;

const RenderPreview = styled.div`
    flex: 1;
    min-height: 150px;
    padding: 14px;
    border: 1px solid #d6e0eb;
    border-radius: 12px;
    background: #fbfdff;
    color: #203249;
    overflow: auto;
`;

const PreviewPlaceholder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #8ba0b5;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
`;

const Message = styled.div`
    min-height: 20px;
    color: ${(props) => (props.$error ? "#b42318" : "#64748b")};
    font-size: 12px;
    line-height: 1.5;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 18px;
    border-top: 1px solid #e2e8f0;
`;

const InputActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: space-between;
`;

const InputActionGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Button = styled.button`
    height: 32px;
    padding: 0 12px;
    border: 1px solid ${(props) => (props.$primary ? "#12805f" : "#d6e0ea")};
    border-radius: 8px;
    background: ${(props) => (props.$primary ? "#138a6d" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : "#32485e")};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

function ManuscriptMathOCRModal({
    open,
    status,
    latex,
    errorMessage,
    isRunning,
    onRequest,
    onStop,
    onInsert,
    onClose,
    onReset,
}) {
    const inputRef = useRef(null);
    const mathPreviewRef = useRef(null);
    const [imageDataUrl, setImageDataUrl] = useState("");
    const [mimeType, setMimeType] = useState("");
    const [draftLatex, setDraftLatex] = useState("");
    const [localError, setLocalError] = useState("");
    const [previewError, setPreviewError] = useState("");

    useEffect(() => {
        setDraftLatex(latex || "");
    }, [latex]);

    useEffect(() => {
        if (!open) {
            setImageDataUrl("");
            setMimeType("");
            setDraftLatex("");
            setLocalError("");
            setPreviewError("");
        }
    }, [open]);

    useEffect(() => {
        const element = mathPreviewRef.current;
        const latexText = draftLatex.trim();
        let cancelled = false;

        if (!open || !element) {
            return undefined;
        }

        element.innerHTML = "";
        setPreviewError("");

        if (!latexText) {
            return undefined;
        }

        loadMathJax()
            .then(async (MathJax) => {
                if (cancelled) return;
                const svg = await MathJax.tex2svgPromise(latexText, {
                    display: true,
                });
                if (cancelled) return;
                element.innerHTML = "";
                element.appendChild(svg);
            })
            .catch(() => {
                if (!cancelled) {
                    element.innerHTML = "";
                    setPreviewError("MathJax 미리보기를 렌더링하지 못했습니다.");
                }
            });

        return () => {
            cancelled = true;
        };
    }, [draftLatex, open]);

    if (!open) {
        return null;
    }

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type?.startsWith("image/")) {
            setLocalError("이미지 파일만 사용할 수 있습니다.");
            return;
        }

        try {
            const dataUrl = await fileToDataUrl(file);
            setImageDataUrl(dataUrl);
            setMimeType(file.type);
            setLocalError("");
            onReset?.();
        } catch (_error) {
            setLocalError("이미지를 읽지 못했습니다.");
        }
    };

    const handlePaste = (event) => {
        const item = Array.from(event.clipboardData?.items || []).find((entry) => entry.type?.startsWith("image/"));
        const file = item?.getAsFile?.();
        if (file) {
            event.preventDefault();
            void handleFile(file);
        }
    };

    const readClipboardImage = async () => {
        if (!navigator.clipboard?.read) {
            setLocalError("이 브라우저에서는 클립보드 이미지 읽기를 지원하지 않습니다.");
            return;
        }

        try {
            const items = await navigator.clipboard.read();
            for (const item of items) {
                const imageType = item.types.find((type) => type.startsWith("image/"));
                if (!imageType) {
                    continue;
                }
                const blob = await item.getType(imageType);
                await handleFile(new File([blob], "clipboard-image", { type: imageType }));
                return;
            }
            setLocalError("클립보드에서 이미지 데이터를 찾지 못했습니다.");
        } catch (_error) {
            setLocalError("클립보드 이미지를 읽지 못했습니다.");
        }
    };

    const message =
        localError ||
        errorMessage ||
        (status === "connecting"
            ? "수식 OCR 소켓에 연결 중입니다."
            : status === "running"
              ? "이미지에서 LaTeX 수식을 인식하고 있습니다."
              : status === "completed"
                ? "인식 결과를 확인하고 필요하면 수정하세요."
                : "");

    return createPortal(
        <Backdrop onPaste={handlePaste}>
            <Shell role="dialog" aria-label="수식 이미지 인식">
                <Header>
                    <Title>수식 이미지 인식</Title>
                    <Button type="button" onClick={onClose}>
                        닫기
                    </Button>
                </Header>
                <Body>
                    <ImageColumn>
                        <SectionTitle>이미지</SectionTitle>
                        <DropZone
                            className="math-ocr-drop-zone"
                            onClick={() => inputRef.current?.click()}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                                event.preventDefault();
                                void handleFile(event.dataTransfer?.files?.[0]);
                            }}
                        >
                            {imageDataUrl ? (
                                <PreviewImage src={imageDataUrl} alt="수식 이미지 미리보기" />
                            ) : (
                                "이미지를 드래그앤드롭하거나 파일을 선택하세요."
                            )}
                        </DropZone>
                        <InputActions>
                            <InputActionGroup>
                                <Button type="button" onClick={() => inputRef.current?.click()}>
                                    파일 선택
                                </Button>
                                <Button type="button" onClick={readClipboardImage}>
                                    클립보드에서 가져오기
                                </Button>
                            </InputActionGroup>
                            <Button
                                type="button"
                                disabled={!imageDataUrl || isRunning}
                                onClick={() => onRequest({ imageDataUrl, mimeType })}
                            >
                                인식 시작
                            </Button>
                        </InputActions>
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(event) => {
                                void handleFile(event.target.files?.[0]);
                                event.target.value = "";
                            }}
                        />
                        {message ? <Message $error={Boolean(localError || errorMessage)}>{message}</Message> : null}
                    </ImageColumn>
                    <ResultColumn>
                        <ResultSection>
                            <SectionTitle>LaTeX 결과</SectionTitle>
                            <Textarea
                                value={draftLatex}
                                onChange={(event) => setDraftLatex(event.target.value)}
                                placeholder="이미지를 인식하면 LaTeX 수식이 여기에 표시됩니다."
                            />
                        </ResultSection>
                        <ResultSection>
                            <SectionTitle>미리보기</SectionTitle>
                            <RenderPreview>
                                {draftLatex.trim() ? null : (
                                    <PreviewPlaceholder>LaTeX 결과가 여기에 렌더링됩니다.</PreviewPlaceholder>
                                )}
                                <div ref={mathPreviewRef} />
                                {previewError ? <PreviewPlaceholder>{previewError}</PreviewPlaceholder> : null}
                            </RenderPreview>
                        </ResultSection>
                    </ResultColumn>
                </Body>
                <Footer>
                    {isRunning ? (
                        <Button type="button" onClick={onStop}>
                            중지
                        </Button>
                    ) : null}
                    <Button
                        type="button"
                        $primary
                        disabled={!draftLatex.trim() || isRunning}
                        onClick={() => onInsert(draftLatex.trim())}
                    >
                        블록 수식 삽입
                    </Button>
                </Footer>
            </Shell>
        </Backdrop>,
        document.body,
    );
}

export default ManuscriptMathOCRModal;
